import { useId, useRef, useState } from 'react';
import { Paperclip } from 'lucide-react';
import { uploadFinanceReceipt, type ReceiptTarget } from '@/lib/finance';
import { errorMessage } from '@/lib/errors';
import { cn } from '@/lib/utils';

export interface ReceiptUploadButtonProps {
  /** Exactly one target (§14, lib/finance.ts's ReceiptTarget) — the row this receipt belongs to. */
  target: ReceiptTarget;
  /** Called once per file that uploads successfully, in selection order. */
  onUploaded: (attachmentId: string, file: File) => void;
  onError?: (message: string) => void;
  disabled?: boolean;
  multiple?: boolean;
  label?: string;
  className?: string;
  /**
   * Forwarded to the native input's `capture` attribute — on a phone this
   * opens the camera directly instead of the file/gallery picker (spec
   * §8.3's submission form). Omitted (the default) for every existing
   * desk-bound caller, which still gets the ordinary file chooser.
   */
  capture?: 'environment' | 'user';
}

// Same jpeg/png/webp/pdf list spec §14 and lib/finance.ts's (unexported)
// isReceiptMime check against — kept in sync by hand since finance.ts does
// not export its constant; the `accept` attribute is a UX hint only (the
// mime is still checked for real by uploadFinanceReceipt before any network
// call, and again server-side at register time).
const ACCEPT = 'image/jpeg,image/png,image/webp,application/pdf';

/**
 * Upload one or more receipts against a single target (spec §8.2/§8.3/§14).
 * Re-authors ApartmentPhotos.tsx's hidden-input-plus-label pattern (a native
 * file input styled by a `<label>`, not by the input itself — file inputs
 * cannot be restyled directly) for the finance section's private bucket.
 *
 * Uploads run one file at a time and stop at the first failure, same as
 * ApartmentPhotos: a partial batch with a clear "which one failed" beats a
 * Promise.all where one 422 hides which of five files caused it.
 */
export function ReceiptUploadButton({
  target,
  onUploaded,
  onError,
  disabled,
  multiple = true,
  label = 'Attach receipt',
  className,
  capture,
}: ReceiptUploadButtonProps) {
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  async function onFiles(files: FileList | null) {
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      for (const file of Array.from(files)) {
        try {
          const id = await uploadFinanceReceipt(file, target);
          onUploaded(id, file);
        } catch (err) {
          onError?.(errorMessage(err));
          return;
        }
      }
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  }

  return (
    <div className={className}>
      <label
        htmlFor={inputId}
        aria-busy={uploading || undefined}
        className={cn(
          'inline-flex h-8 cursor-pointer items-center gap-2 rounded-[var(--radius-control)] border border-border bg-card px-4 text-sm font-semibold text-primary transition',
          'hover:bg-[var(--ollio-navy-light)] hover:border-primary',
          (disabled || uploading) && 'pointer-events-none opacity-55',
        )}
      >
        <Paperclip className="h-4 w-4" aria-hidden="true" />
        {uploading ? 'Uploading…' : label}
      </label>
      <input
        id={inputId}
        ref={inputRef}
        type="file"
        accept={ACCEPT}
        multiple={multiple}
        capture={capture}
        disabled={disabled || uploading}
        onChange={(e) => void onFiles(e.target.files)}
        className="hidden"
      />
    </div>
  );
}
